<script setup lang="ts">
import {
  useDebounce,
  useIntersectionObserver,
  useResizeObserver,
} from '@vueuse/core'
import {
  defineComponent,
  h,
  onMounted,
  ref,
  watch,
  type CSSProperties,
  computed,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { PopupBasicHeader, SearchBox } from 'wepin-vue-component'
import { WrappedTokenListItem } from '@/components'
import { useBreakPointsClass } from '@/composables'
import { defaultBreakPoints } from '@/const'
import {
  useCoinAPIStore,
  useCoinStore,
  useUserAPIStore,
  useWalletStore,
} from '@/stores'
import type { BaseCoin, Maybe, Promisable } from '@/types'
import { Coin, Token } from '@/types'
import {
  isV1SupportedCoin,
  isV1SupportedTokenType,
  isHiddenFromAddAccount,
  isHiddenTokenTypeFromAddAccount,
} from '@/utils/v1-asset-filter'

const emits = defineEmits<{
  (event: 'close'): void
  (event: 'click-coin', coin: Coin): void
  (
    event: 'click-token',
    params: {
      token: Token
      network?: string
    },
  ): void
}>()

const { t } = useI18n()

const coinAPIStore = useCoinAPIStore()
const walletStore = useWalletStore()
const coinStore = useCoinStore()
const userAPIStore = useUserAPIStore()
const { breakPointsClass } = useBreakPointsClass(defaultBreakPoints)
const router = useRouter()
const infiniteScrollEl = ref<HTMLElement>()
const isSearching = ref<boolean>(false)
const gotAllTokens = ref<boolean>(false)
const searchedToken = ref<Token[]>([])
const printingToken = ref<Token[]>([])
const topRef = ref<HTMLDivElement>()

onMounted(async () => {
  // V2: 코인 리스트 로드
  if (!coinStore.coinList || coinStore.coinList.length === 0) {
    await coinAPIStore.fetchCoinList({
      coinIds: [],
      force: true,
      needToAddData: true,
      locale: localeId.value,
      reset: true,
    })
  }

  // 코인 리스트 필터링 업데이트
  filteredCoinList.value = deDuplicationCoinList()

  if (router.currentRoute.value.query.preSelectedCoin) {
    const coin: Coin | undefined = coinStore.coinList!.find(
      (coin) =>
        coin.id.toString() === router.currentRoute.value.query.preSelectedCoin,
    )
    emits('click-coin', coin!)
  }
})

let page = 1

const localeId = computed(() => userAPIStore.userInfo?.localeId as number)

async function fetchNextTokens() {
  if (isSearching.value || gotAllTokens.value) return
  isSearching.value = true

  const response = await coinAPIStore.searchTokenList({
    searchValue: debouncedSearchValue.value,
    page: page,
    gotAllTokens: gotAllTokens,
    locale: localeId.value,
  })

  const accounts = walletStore.getAccountList({
    walletId: router.currentRoute.value.params.walletId as string,
  })
  const filteredTokenList = response.filter((tokenInfo: Token) => {
    if ((tokenInfo.property as any)?.isAppOnly) return false

    // V1 지원 토큰 타입만 표시
    // V2: property._v2CurrencyId 형식은 "KLAYTN-ERC20/0x..." → "/" 앞이 deviceStoreId
    const v2TokenId = (tokenInfo.property as any)?._v2CurrencyId || ''
    const deviceStoreId = v2TokenId.includes('/')
      ? v2TokenId.split('/')[0]
      : (tokenInfo as any).deviceStoreId || ''
    if (deviceStoreId && !isV1SupportedTokenType(deviceStoreId)) return false

    // 계정 추가에서 숨김 처리된 토큰 타입 제외 (near_pt_token, near_pt_testnet_token)
    if (deviceStoreId && isHiddenTokenTypeFromAddAccount(deviceStoreId))
      return false

    // 부모 네트워크가 V1 지원인지 확인
    // V2: coin.property._v2CurrencyId 또는 coin.network
    const parentCurrencyId =
      (tokenInfo.coin?.property as any)?._v2CurrencyId ||
      tokenInfo.coin?.network ||
      ''
    if (parentCurrencyId && !isV1SupportedCoin(parentCurrencyId)) return false

    // 부모 네트워크가 계정 추가에서 숨김 처리된 경우 제외
    if (parentCurrencyId && isHiddenFromAddAccount(parentCurrencyId))
      return false

    // 이미 해당 토큰 계정이 있으면 제외
    // TokenAccount는 walletStore.accountTokenList에서 확인 (TypeAccount와 분리 저장됨)
    // 중요: 현재 지갑의 토큰 계정만 확인해야 함 (다른 지갑의 토큰은 무시)
    const currentWalletId = router.currentRoute.value.params.walletId as string
    const hasTokenAccount = walletStore.accountTokenList.some((tokenAccount) => {
      const tokenWalletId = (tokenAccount as any).account?.walletId || (tokenAccount as any).walletId || ''
      if (tokenWalletId && tokenWalletId !== currentWalletId) return false
      const tokenCurrencyId = (tokenAccount as any).currencyId || ''
      // 대소문자 무시하고 비교
      return tokenCurrencyId.toLowerCase() === v2TokenId.toLowerCase()
    })

    return !hasTokenAccount
  })

  searchedToken.value = searchedToken.value.concat(filteredTokenList)
  if (!scrollToTop.value) printingToken.value = searchedToken.value
  page++
  isSearching.value = false
}

const onExit = () => {
  emits('close')
}

const onClickCoin = (coin: Coin) => {
  emits('click-coin', coin)
}

const onClickToken = async (token: Token) => {
  const networkAccount = await walletStore.getAccountListFilteringByNetwork({
    network: token.coin?.network!,
  })

  if (!networkAccount || networkAccount.length === 0) {
    emits('click-token', {
      token,
      network: undefined,
    })
  } else {
    emits('click-token', {
      token,
      network: token.coin?.network,
    })
  }
}

const deDuplicationCoinList = () => {
  const accounts = walletStore.getAccountList({
    walletId: router.currentRoute.value.params.walletId as string,
  })
  const deDuplicationList =
    coinStore.coinList?.filter((coin) => {
      if ((coin.property as any)?.isAppOnly) return false

      // V1 지원 네트워크만 표시
      // V2: property._v2CurrencyId에 Currency ID 저장 (예: "ETHEREUM")
      const currencyId =
        (coin.property as any)?._v2CurrencyId || coin.network || ''
      if (!isV1SupportedCoin(currencyId)) return false

      // 계정 추가에서 숨김 처리된 네트워크 제외 (near_pt, near_pt_testnet)
      if (isHiddenFromAddAccount(currencyId)) return false

      // 이미 해당 네트워크 계정이 있으면 제외
      // V2: account.currencyId와 coin의 V2 Currency ID 비교
      return !accounts?.some((account) => {
        const accountCurrencyId = (account as any).currencyId || ''
        // 토큰 계정은 "ERC20/0x..." 형식이므로 코인 비교에서 제외
        if (accountCurrencyId.includes('/')) return false
        return accountCurrencyId === currencyId
      })
    }) ?? []
  return deDuplicationList
}

const filterCoinList = <T extends BaseCoin>(
  coinList: Maybe<T[]>,
): Maybe<T[]> => {
  return coinList?.filter((coin) => {
    return (
      coin.name
        ?.toLowerCase()
        .includes(debouncedSearchValue.value.toLowerCase()) ||
      coin.symbol
        ?.toLowerCase()
        .includes(debouncedSearchValue.value.toLowerCase())
    )
  })
}
const searchValue = ref('')
const debouncedSearchValue = useDebounce(searchValue, 500)

watch(
  debouncedSearchValue,
  async () => {
    gotAllTokens.value = false
    page = 1
  },
  { immediate: true },
)

const filteredCoinList = ref<Maybe<Coin[]>>(deDuplicationCoinList())
const scrollToTop = ref<boolean>(false)

watch(searchValue, () => {
  scrollToTop.value = true
})

// watch(filteredCoinList, () => {
//   if (filteredCoinList.value?.length === 0) fetchNextTokens()
// })

const isTopIntersecting = ref<boolean>(false)

useIntersectionObserver(topRef, ([{ isIntersecting }]) => {
  isTopIntersecting.value = isIntersecting
})

useIntersectionObserver(infiniteScrollEl, ([{ isIntersecting }]) => {
  if (isIntersecting) {
    fetchNextTokens()
  }
})

const scrollRef = ref<HTMLElement>()

watch(debouncedSearchValue, () => {
  if (isTopIntersecting.value) {
    scrollToTop.value = false
    if (debouncedSearchValue.value === '') {
      filteredCoinList.value = deDuplicationCoinList()
    } else {
      filteredCoinList.value = filterCoinList(deDuplicationCoinList())
    }

    searchedToken.value = []
    printingToken.value = []
    return
  }

  let id: any
  if (scrollToTop.value) {
    scrollRef.value!.onscroll = (event: Event) => {
      clearTimeout(id)
      id = setTimeout(() => {
        if (debouncedSearchValue.value === '') {
          filteredCoinList.value = deDuplicationCoinList()
          printingToken.value = searchedToken.value
        } else {
          filteredCoinList.value = filterCoinList(deDuplicationCoinList())
          printingToken.value = searchedToken.value
        }
        scrollToTop.value = false
        searchedToken.value = []
        printingToken.value = []
        scrollRef.value!.onscroll = () => {}
      }, 50)
    }

    // if (!filterCoinList(deDuplicationCoinList())?.length) {
    //   fetchNextTokens()
    // }
    topRef.value?.scrollIntoView({ behavior: 'smooth' })
    scrollToTop.value = false
  }
})

const enableTestnetChip = (currency: Coin | Token) => {
  if (currency instanceof Coin) {
    return currency.isTestnet ? t('create_account.testnet') : ''
  } else {
    return currency.coin?.isTestnet ? t('create_account.testnet') : ''
  }
}

const listEl = ref(null)

const listHeight = ref<number>(465)

useResizeObserver(listEl, (entries) => {
  const entry = entries[0]
  listHeight.value = entry.contentRect.height
})

const listHeightStyle = ref<CSSProperties>({
  height:
    breakPointsClass.value === 'mobile'
      ? `${window.screen.availHeight - 174}px`
      : '465px',
})
watch([breakPointsClass, debouncedSearchValue, listHeight], () => {
  if (isSearching.value) return
  if (breakPointsClass.value !== 'mobile') {
    const height = debouncedSearchValue.value
      ? Math.min(465, listHeight.value)
      : 465
    listHeightStyle.value = {
      height: `${height}px`,
    }
  }
})
</script>
<script lang="ts">
// eslint-disable-next-line import/order
import CoinList from './CoinList.vue'
export const createCoinList = (params: {
  onClickCoin: (coin: Coin) => Promisable<void>
  onClickToken: (params: { token: Token; network?: string }) => Promisable<void>
}) => {
  return defineComponent<typeof CoinList>((props) => {
    return () =>
      h(CoinList, {
        'onClick-coin': params.onClickCoin,
        'onClick-token': params.onClickToken,
        ...props,
      })
  })
}
</script>
<template>
  <div class="coin-list" :class="[breakPointsClass]">
    <PopupBasicHeader
      class="popup-header"
      :title="t('create_account.add_account')"
      :exit="onExit"
    />
    <div class="flex-between default-padding">
      {{ t('create_account.choose_account') }}
    </div>
    <div class="default-padding">
      <SearchBox
        v-model="searchValue"
        width="100%"
        class="search-box"
        :placeholder="t('send.search')"
      />
    </div>

    <div
      ref="scrollRef"
      class="account-list"
      :class="[breakPointsClass]"
      :style="listHeightStyle"
    >
      <div ref="listEl">
        <div ref="topRef" />
        <div
          v-for="coin of filteredCoinList"
          :key="coin.id"
          class="add-coin-box"
          @click="onClickCoin(coin)"
        >
          <WrappedTokenListItem
            class="token-list-item"
            :class="enableTestnetChip(coin) ? 'testnet-chip' : ''"
            :ticker="coin.name"
            :symbol="coin.symbol"
            :ticker-src="coin.iconUrl"
            :chip-testnet="enableTestnetChip(coin)"
          />
        </div>
        <template v-for="token of printingToken" :key="token.id">
          <div class="add-coin-box" @click="onClickToken(token)">
            <WrappedTokenListItem
              class="token-list-item"
              :class="enableTestnetChip(token) ? 'testnet-chip' : ''"
              :ticker="token.name"
              :symbol="token.symbol"
              :chip="token.coin?.name"
              :chip-testnet="enableTestnetChip(token)"
              :ticker-src="token.iconUrl"
              :network-src="token.coin?.iconUrl"
            />
          </div>
        </template>
        <div v-if="!gotAllTokens" ref="infiniteScrollEl">
          <div v-for="(num, index) in 5" :key="index" class="skeleton">
            <div class="circle" />
            <div class="rect" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.skeleton {
  padding: 20px 30px;
  align-items: center;
  display: flex;
  flex-direction: row;

  .circle {
    margin-right: 10px;
    width: 30px;
    height: 30px;
    border-radius: 100%;
    background-color: var(--bg_main);
  }

  .rect {
    width: 121px;
    height: 24px;
    border-radius: 4px;
    background-color: var(--bg_main);
  }
}
.coin-list {
  padding-bottom: 30px;

  .flex-between {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-family: SUIT;
  }

  .search-box {
    margin: 20px 0;
  }
}

.account-list {
  margin-right: 5px;
  overflow-x: hidden;
  transition: all 0.3s ease-in-out;
  .add-coin-box {
    padding: {
      top: 5px;
      bottom: 5px;
    }
  }
  .add-coin-box:hover {
    cursor: pointer;
    background-color: var(--bg_main);
  }

  &::-webkit-scrollbar {
    width: 5px;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--grey_999);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background-color: var(--line_01);
    border-radius: 3px;
  }
  &:deep(.listShort) {
    width: 100%;
    cursor: pointer;
    &:hover {
      background-color: var(--bg_main);
    }
  }
}

.default-padding {
  padding: 0 30px;
}

.coin-list.mobile {
  height: 100vh;
  padding-bottom: 0px !important;
  .default-padding {
    padding: 0 20px;
  }
  &:deep(.token-list-item) {
    padding: {
      top: 17px;
      bottom: 17px;
      left: 20px;
      right: 20px;
    }
  }
}
.account-list.mobile {
  overflow-y: scroll;
}
</style>
